import { z } from 'zod';

/**
 * What an operator has configured to happen when a captured signal matches a
 * **banned** account (`trust.signals.<type>.action`, U9/U15).
 *
 * - `flag` — the capture succeeds, a `pending_review` match row is raised, and a
 *   human decides in the Trust queue.
 * - `block` — the act is refused outright with a deliberately neutral error, and
 *   the match is still recorded so staff can see what was attempted.
 *
 * Only ever evaluated against signals of banned accounts, so a false positive
 * means somebody re-used a banned member's device, address or document — not
 * that an ordinary member collided with an ordinary member.
 */
export const MATCH_ACTIONS = ['flag', 'block'] as const;

export const MatchActionSchema = z.enum(MATCH_ACTIONS);

export type MatchAction = (typeof MATCH_ACTIONS)[number];

/**
 * What a recorded match says actually happened — the **past tense**, and a
 * deliberately separate vocabulary from {@link MATCH_ACTIONS}.
 *
 * The policy is a setting and settings move. If a row stored the policy value
 * rather than the outcome, an operator switching `device_fingerprint` from
 * `block` to `flag` would silently re-describe every match already recorded
 * under the old policy — the same reason `face_match_checks.thresholdAtTime` is
 * a snapshot rather than a live read.
 */
export const MATCH_ACTIONS_TAKEN = ['flagged', 'blocked'] as const;

export const MatchActionTakenSchema = z.enum(MATCH_ACTIONS_TAKEN);

export type MatchActionTaken = (typeof MATCH_ACTIONS_TAKEN)[number];

const TAKEN_FOR: Readonly<Record<MatchAction, MatchActionTaken>> = {
  flag: 'flagged',
  block: 'blocked',
};

/** The record to write for a policy that has just been applied. */
export function actionTakenFor(action: MatchAction): MatchActionTaken {
  return TAKEN_FOR[action];
}

/**
 * Whether this policy refuses the act in progress.
 *
 * A predicate rather than an inline `=== 'block'` at each call site, so the
 * question is asked in one voice — and so a future third action (a silent
 * shadow mode, say) changes one function instead of every branch.
 */
export function isBlockingAction(action: MatchAction): boolean {
  return action === 'block';
}
